import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { signIn, signUp, signInWithOAuth, type OAuthProvider } from "@/services/auth";

export default function Home() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const formEl = e.currentTarget;
    const data = new FormData(formEl);
    const honeypot = (data.get("website_alt") as string | null)?.trim();
    if (honeypot) return;
    const email = ((data.get("email") as string) || "").trim();
    const password = ((data.get("password") as string) || "").trim();
    if (!email || !password) {
      setError("Completa tu correo/WhatsApp y contraseña para continuar.");
      return;
    }
    if (mode === "signup" && password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "signup") {
        const result = await signUp(email, password);
        if (result.session) {
          // Confirmación de correo deshabilitada: ya queda logueado.
          navigate("/app/dashboard");
        } else {
          // Supabase exige confirmar el correo antes de iniciar sesión.
          setInfo(
            "¡Cuenta creada! Revisa tu correo y confirma tu cuenta antes de iniciar sesión."
          );
          setMode("login");
          formEl.reset();
        }
      } else {
        await signIn(email, password);
        navigate("/app/dashboard");
      }
    } catch (err) {
      const message =
        err instanceof Error && /invalid login credentials/i.test(err.message)
          ? "Correo o contraseña incorrectos."
          : err instanceof Error && /already registered|user already exists/i.test(err.message)
          ? "Ese correo ya tiene una cuenta. Intenta iniciar sesión."
          : err instanceof Error
          ? err.message
          : mode === "signup"
          ? "No se pudo crear la cuenta. Intenta de nuevo."
          : "No se pudo iniciar sesión. Intenta de nuevo.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: OAuthProvider) => {
    setError(null);
    setInfo(null);
    setOauthLoading(provider);
    try {
      await signInWithOAuth(provider);
      // Si todo va bien, el navegador se redirige al proveedor y esta función
      // nunca llega a "finally" porque la página se navega fuera.
    } catch (err) {
      const providerName =
        provider === "google" ? "Google" : provider === "apple" ? "Apple" : "Facebook";
      const message =
        err instanceof Error && /provider is not enabled/i.test(err.message)
          ? `El inicio de sesión con ${providerName} todavía no está activado. Intenta con tu correo y contraseña por ahora.`
          : err instanceof Error
          ? err.message
          : `No se pudo iniciar sesión con ${providerName}. Intenta de nuevo.`;
      setError(message);
      setOauthLoading(null);
    }
  };

  const toggleMode = () => {
    setError(null);
    setInfo(null);
    setMode((m) => (m === "login" ? "signup" : "login"));
  };

  return (
    <div className="min-h-screen bg-[#060B26] relative overflow-hidden flex flex-col">
      {/* Circuit lines left */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-40 opacity-60">
        <svg viewBox="0 0 160 800" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 L60 120 L60 200 L120 200 L120 280 L60 280 L60 400 L140 400" stroke="#00C853" strokeWidth="1.5" fill="none" opacity="0.4" />
          <circle cx="60" cy="120" r="3" fill="#00C853" opacity="0.5" />
          <circle cx="120" cy="200" r="3" fill="#00C853" opacity="0.5" />
          <circle cx="60" cy="280" r="3" fill="#00C853" opacity="0.5" />
          <circle cx="140" cy="400" r="3" fill="#00C853" opacity="0.5" />

          <path d="M0 300 L40 300 L40 350 L90 350 L90 450 L30 450 L30 550" stroke="#00B0FF" strokeWidth="1" fill="none" opacity="0.3" />
          <circle cx="40" cy="300" r="2.5" fill="#00B0FF" opacity="0.4" />
          <circle cx="90" cy="350" r="2.5" fill="#00B0FF" opacity="0.4" />
          <circle cx="30" cy="450" r="2.5" fill="#00B0FF" opacity="0.4" />

          <path d="M20 600 L70 600 L70 680 L130 680 L130 750" stroke="#7C4DFF" strokeWidth="1" fill="none" opacity="0.25" />
          <circle cx="70" cy="600" r="2" fill="#7C4DFF" opacity="0.3" />
          <circle cx="130" cy="680" r="2" fill="#7C4DFF" opacity="0.3" />
        </svg>
      </div>

      {/* Circuit lines right */}
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-40 opacity-60">
        <svg viewBox="0 0 160 800" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <path d="M160 100 L100 100 L100 180 L40 180 L40 260 L100 260 L100 380 L20 380" stroke="#7C4DFF" strokeWidth="1.5" fill="none" opacity="0.4" />
          <circle cx="100" cy="100" r="3" fill="#7C4DFF" opacity="0.5" />
          <circle cx="40" cy="180" r="3" fill="#7C4DFF" opacity="0.5" />
          <circle cx="100" cy="260" r="3" fill="#7C4DFF" opacity="0.5" />
          <circle cx="20" cy="380" r="3" fill="#7C4DFF" opacity="0.5" />

          <path d="M160 350 L110 350 L110 420 L50 420 L50 500 L120 500" stroke="#00B0FF" strokeWidth="1" fill="none" opacity="0.3" />
          <circle cx="110" cy="350" r="2.5" fill="#00B0FF" opacity="0.4" />
          <circle cx="50" cy="420" r="2.5" fill="#00B0FF" opacity="0.4" />
          <circle cx="120" cy="500" r="2.5" fill="#00B0FF" opacity="0.4" />

          <path d="M140 580 L80 580 L80 660 L30 660 L30 720" stroke="#00C853" strokeWidth="1" fill="none" opacity="0.25" />
          <circle cx="80" cy="580" r="2" fill="#00C853" opacity="0.3" />
          <circle cx="30" cy="660" r="2" fill="#00C853" opacity="0.3" />
        </svg>
      </div>

      {/* Bottom-right decorative shape */}
      <div className="pointer-events-none absolute -bottom-20 -right-20 w-80 h-80 opacity-40">
        <div className="w-full h-full rounded-full bg-gradient-to-tl from-primary-500 via-accent-500 to-transparent blur-3xl"></div>
      </div>

      {/* Top-right decorative glow */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 opacity-30">
        <div className="w-full h-full rounded-full bg-gradient-to-bl from-accent-500 via-secondary-500 to-transparent blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-6 max-w-md mx-auto w-full">
        {/* Brand */}
        <BrandLogo className="mb-6" />

        {/* Slogan */}
        <div className="text-center mb-6 font-heading">
          <p className="text-2xl md:text-3xl text-white font-semibold leading-tight">No solo existas.</p>
          <p className="text-2xl md:text-3xl text-primary-400 font-extrabold leading-tight">Vívelo.</p>
        </div>

        {/* Login / signup card */}
        <div className="w-full rounded-2xl bg-[#0B1330]/90 border border-[#1a2a5e] p-5 md:p-7">
          <div className="text-center mb-5">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-white">
              {mode === "signup" ? "Crear cuenta" : "Iniciar sesión"}
            </h1>
            <p className="text-foreground-300 text-sm mt-1">
              {mode === "signup" ? "Regístrate y empieza a crecer" : "Accede a tu cuenta y sigue creciendo"}
            </p>
          </div>

          {info ? (
            <div className="text-sm text-primary-300 bg-primary-900/30 border border-primary-700/40 rounded-lg px-3 py-2 mb-3.5">
              {info}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} data-readdy-form id="vivelo-login-form" className="space-y-3.5">
            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="vv-safe-field"
            />

            <div className="relative">
              <span className="absolute inset-y-0 left-3.5 flex items-center w-5 h-5 my-auto">
                <i className="ri-user-line text-foreground-400"></i>
              </span>
              <input
                name="email"
                type="text"
                placeholder="Correo electrónico o WhatsApp"
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#0d1a3a] border border-[#1e3260] text-white placeholder:text-foreground-400 text-sm outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-3.5 flex items-center w-5 h-5 my-auto">
                <i className="ri-lock-2-line text-foreground-400"></i>
              </span>
              <input
                name="password"
                type={showPass ? "text" : "password"}
                placeholder="Contraseña"
                required
                className="w-full pl-10 pr-10 py-3 rounded-xl bg-[#0d1a3a] border border-[#1e3260] text-white placeholder:text-foreground-400 text-sm outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute inset-y-0 right-3 flex items-center w-8 h-8 justify-center my-auto rounded-full hover:bg-background-800/50 cursor-pointer"
              >
                <i className={`${showPass ? "ri-eye-off-line" : "ri-eye-line"} text-foreground-400`}></i>
              </button>
            </div>

            {mode === "login" ? (
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer text-foreground-300">
                  <input
                    type="checkbox"
                    name="remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 accent-primary-500 rounded"
                  />
                  <span>Recordarme</span>
                </label>
                <button type="button" className="text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            ) : (
              <p className="text-xs text-foreground-400">Mínimo 6 caracteres.</p>
            )}

            {error ? (
              <div className="text-sm text-accent-300 bg-accent-900/40 border border-accent-700/40 rounded-lg px-3 py-2">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading || oauthLoading !== null}
              className="w-full py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-background-950 font-heading font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-70 transition-colors"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>{" "}
                  {mode === "signup" ? "Creando cuenta..." : "Entrando..."}
                </>
              ) : (
                <>
                  {mode === "signup" ? "Crear cuenta" : "Iniciar sesión"} <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#1e3260]"></div>
            <span className="text-xs text-foreground-300">o continúa con</span>
            <div className="flex-1 h-px bg-[#1e3260]"></div>
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            <button
              type="button"
              onClick={() => handleOAuth("google")}
              disabled={loading || oauthLoading !== null}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0d1a3a] border border-[#1e3260] text-white text-sm hover:border-primary-500 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {oauthLoading === "google" ? (
                <i className="ri-loader-4-line animate-spin text-sm"></i>
              ) : (
                <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
              )}
              <span className="text-sm">Google</span>
            </button>
            <button
              type="button"
              onClick={() => handleOAuth("apple")}
              disabled={loading || oauthLoading !== null}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0d1a3a] border border-[#1e3260] text-white text-sm hover:border-primary-500 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {oauthLoading === "apple" ? (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              ) : (
                <i className="ri-apple-fill text-base"></i>
              )}
              <span className="text-sm">Apple</span>
            </button>
            <button
              type="button"
              onClick={() => handleOAuth("facebook")}
              disabled={loading || oauthLoading !== null}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0d1a3a] border border-[#1e3260] text-white text-sm hover:border-primary-500 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {oauthLoading === "facebook" ? (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              ) : (
                <i className="ri-facebook-circle-fill text-base text-[#1877F2]"></i>
              )}
              <span className="text-sm">Facebook</span>
            </button>
          </div>

          <div className="text-center mt-5 text-sm text-foreground-300">
            {mode === "signup" ? (
              <>
                ¿Ya tienes cuenta?{" "}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-primary-400 hover:text-primary-300 font-semibold cursor-pointer"
                >
                  Iniciar sesión
                </button>
              </>
            ) : (
              <>
                ¿No tienes cuenta?{" "}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-primary-400 hover:text-primary-300 font-semibold cursor-pointer"
                >
                  Crear cuenta
                </button>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-5 text-xs text-foreground-300 flex flex-col items-center gap-1">
          <div className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-primary-400"></i>
            <span>Tu información está protegida</span>
          </div>
          <span>Privacidad y seguridad al 100%</span>
        </div>
      </div>
    </div>
  );
}
